// AnimatedRoutes.js
import React from "react";
import { Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import Home from "./components/sites/Home";
import News from "./components/sites/News";
import Tests from "./components/sites/Tests";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import Community from "./components/sites/Community";
import AdBlockWarning from "./components/common/AdBlockWarning";
import Imprint from "./components/sites/Imprint";
import PrivacyPolicy from "./components/sites/PrivacyPolicy";
import TermsOfUse from "./components/sites/TermsOfUse";
import Contact from "./components/sites/Contact";
import Dashboard from "./components/dashboard/Dashboard";
import AboutUs from "./components/sites/AboutUs";
import Careers from "./components/sites/Careers";
import Team from "./components/sites/Team";

import Footer from "./components/common/Footer";

import ProtectedNewsArticleEditorYoopta from "./components/editor/ProtectedNewsArticleEditorYoopta";
import { useAuth } from "./context/AuthContext";

// einfacher Rollen-Guard (IDs: 1=Admin, 2=Redakteur, 3=Moderator)
function RequireRole({ allowedIds = [1, 2, 3], children }) {
    const { roleId } = useAuth();
    const ok = allowedIds.includes(Number(roleId));
    return ok ? children : <Navigate to="/login" replace />;
}

function AnimatedRoutes() {
    const location = useLocation();

    const pageTransition = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
        transition: { duration: 0.25 },
    };

    // Wrapper-Seiten für den Editor (Create/Edit) mit gleichem Layout
    function EditorCreatePage() {
        return (
            <motion.div {...pageTransition}>
                <ProtectedNewsArticleEditorYoopta
                    allowedRoleIds={[1, 2, 3]}
                    mode="create"
                />
                <Footer />
            </motion.div>
        );
    }

    function EditorEditPage() {
        const { siteName } = useParams();
        return (
            <motion.div {...pageTransition}>
                <ProtectedNewsArticleEditorYoopta
                    allowedRoleIds={[1, 2, 3]}
                    mode="edit"
                    siteName={siteName}
                />
                <Footer />
            </motion.div>
        );
    }

    return (
        <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
                <Route
                    path="/"
                    element={
                        <motion.div {...pageTransition}>
                            <Home />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/news/*"
                    element={
                        <motion.div {...pageTransition}>
                            <News />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/tests/*"
                    element={
                        <motion.div {...pageTransition}>
                            <Tests />
                            <Footer />
                        </motion.div>
                    }
                />

                {/* Editor (nur für 1,2,3) */}
                <Route
                    path="/editor/news"
                    element={
                        <RequireRole allowedIds={[1, 2, 3]}>
                            <EditorCreatePage />
                        </RequireRole>
                    }
                />
                <Route
                    path="/editor/news/:siteName"
                    element={
                        <RequireRole allowedIds={[1, 2, 3]}>
                            <EditorEditPage />
                        </RequireRole>
                    }
                />

                <Route
                    path="/register"
                    element={
                        <motion.div {...pageTransition}>
                            <Register />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/login"
                    element={
                        <motion.div {...pageTransition}>
                            <Login />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/community/*"
                    element={
                        <motion.div {...pageTransition}>
                            <Community />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/adblock-warning"
                    element={
                        <motion.div {...pageTransition}>
                            <AdBlockWarning />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/impressum"
                    element={
                        <motion.div {...pageTransition}>
                            <Imprint />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/datenschutz"
                    element={
                        <motion.div {...pageTransition}>
                            <PrivacyPolicy />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/nutzungsbestimmungen"
                    element={
                        <motion.div {...pageTransition}>
                            <TermsOfUse />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/kontakt"
                    element={
                        <motion.div {...pageTransition}>
                            <Contact />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/info"
                    element={
                        <motion.div {...pageTransition}>
                            <AboutUs />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/karriere"
                    element={
                        <motion.div {...pageTransition}>
                            <Careers />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/team"
                    element={
                        <motion.div {...pageTransition}>
                            <Team />
                            <Footer />
                        </motion.div>
                    }
                />
                <Route
                    path="/dashboard"
                    element={
                        <RequireRole allowedIds={[1, 2, 3]}>
                            <motion.div {...pageTransition}>
                                <Dashboard />
                                <Footer />
                            </motion.div>
                        </RequireRole>
                    }
                />
            </Routes>
        </AnimatePresence>
    );
}

export default AnimatedRoutes;
